// Globals
import { useTranslation } from 'react-i18next';

// Models
import { SocialMedia } from '@/models/settings.model';
import { SupportedLocales } from '@/models/localization.model';

// Modules
import { MapsProvider, MultiLangPlaceAutocomplete } from '@/modules/Maps';

// Helpers
import { emailRegex } from '@/constants/regex';
import { SettingsFormFields, TSettingsFormValues } from '../../helpers/types';
import { useSettingsFormRules } from '../../hooks';

// Components
import { Form, FormItem, IFormInstance, TextEditor } from '@/components';
import { Input, InputNumber, TagInput, TextArea } from '@/ui';
import { StyledBlock, StyledContainer, StyledContentBlock, StyledLabel, StyledTitle } from '../Styled';

type TSettingsFormProps = {
  initialValues: Partial<TSettingsFormValues>;
  onSubmit?: (values: TSettingsFormValues) => void;
  form?: IFormInstance;
};

const SettingsForm = ({ initialValues, onSubmit, form }: TSettingsFormProps) => {
  const { t } = useTranslation(['settings', 'common']);
  const rules = useSettingsFormRules();
  return (
    <StyledContentBlock>
      <Form initialValues={initialValues} onFinish={onSubmit} form={form}>
        <FormItem
          name={SettingsFormFields.ADMIN_EMAILS}
          label={t('admin_emails')}
          rules={[
            ...rules[SettingsFormFields.ADMIN_EMAILS],
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (
                  !value ||
                  (value &&
                    getFieldValue(SettingsFormFields.ADMIN_EMAILS)?.every((item: string) => emailRegex.test(item)))
                ) {
                  return Promise.resolve();
                }

                return Promise.reject(t('validation_email'));
              },
            }),
          ]}
        >
          <TagInput type="email" />
        </FormItem>
        <FormItem
          name={SettingsFormFields.ORDER_EMAILS}
          rules={[
            ...rules[SettingsFormFields.ORDER_EMAILS],
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (
                  !value ||
                  (value &&
                    getFieldValue(SettingsFormFields.ORDER_EMAILS)?.every((item: string) => emailRegex.test(item)))
                ) {
                  return Promise.resolve();
                }

                return Promise.reject(t('validation_email'));
              },
            }),
          ]}
          label={t('order_emails')}
        >
          <TagInput />
        </FormItem>
        <FormItem
          name={SettingsFormFields.PRICE_CHANGE_ADMIN_EMAILS}
          label={t('price_change_admin_emails')}
          rules={[
            ...rules[SettingsFormFields.PRICE_CHANGE_ADMIN_EMAILS],
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (
                  !value ||
                  (value &&
                    getFieldValue(SettingsFormFields.PRICE_CHANGE_ADMIN_EMAILS)?.every((item: string) =>
                      emailRegex.test(item),
                    ))
                ) {
                  return Promise.resolve();
                }

                return Promise.reject(t('validation_email'));
              },
            }),
          ]}
        >
          <TagInput type="email" />
        </FormItem>

        <FormItem
          name={SettingsFormFields.GOOGLE_ANALYTICS}
          rules={rules[SettingsFormFields.GOOGLE_ANALYTICS]}
          label={t('google_analytics')}
        >
          <Input />
        </FormItem>
        <FormItem
          name={SettingsFormFields.MIN_ORDER_TOTAL}
          rules={rules[SettingsFormFields.MIN_ORDER_TOTAL]}
          label={t('min_order_total')}
        >
          <InputNumber min={0.01} step={0.01} precision={2} style={{ width: '100%' }} />
        </FormItem>
        <StyledContainer>
          <StyledBlock>
            <StyledLabel>{t('support_service')}</StyledLabel>
            <FormItem
              name={SettingsFormFields.CONTACT_PHONE}
              rules={rules[SettingsFormFields.CONTACT_PHONE]}
              label={t('contact_phone')}
            >
              <Input />
            </FormItem>
            <FormItem
              name={SettingsFormFields.CONTACT_EMAIL}
              rules={rules[SettingsFormFields.CONTACT_EMAIL]}
              label={t('contact_email')}
            >
              <Input />
            </FormItem>
            <FormItem
              name={[SettingsFormFields.LOCALE, SupportedLocales.UK, SettingsFormFields.WORKING_HOURS]}
              rules={rules[SettingsFormFields.WORKING_HOURS]}
              label={t('working_hours_name_label_ua')}
            >
              <TextEditor isSmall />
            </FormItem>
            <FormItem
              name={[SettingsFormFields.LOCALE, SupportedLocales.RU, SettingsFormFields.WORKING_HOURS]}
              rules={rules[SettingsFormFields.WORKING_HOURS]}
              label={t('working_hours_name_label_ru')}
            >
              <TextEditor isSmall />
            </FormItem>
            <MapsProvider>
              <StyledTitle>{t('merchant_title')}</StyledTitle>
              <FormItem
                name={SettingsFormFields.ADDRESS}
                rules={rules[SettingsFormFields.ADDRESS]}
                label={t('locale_form_address_label')}
              >
                <MultiLangPlaceAutocomplete withCityName />
              </FormItem>
            </MapsProvider>
            <FormItem
              name={SettingsFormFields.FOP_ID}
              rules={rules[SettingsFormFields.FOP_ID]}
              label={t('fop_merchant_id')}
            >
              <Input />
            </FormItem>
            <FormItem
              name={SettingsFormFields.FOP_DESCRIPTION}
              rules={rules[SettingsFormFields.FOP_DESCRIPTION]}
              label={t('fop_merchant_title')}
            >
              <TextArea />
            </FormItem>
            <FormItem
              name={SettingsFormFields.TOV_ID}
              rules={rules[SettingsFormFields.FOP_ID]}
              label={t('tov_merchant_id')}
            >
              <Input />
            </FormItem>
            <FormItem
              name={SettingsFormFields.TOV_DESCRIPTION}
              rules={rules[SettingsFormFields.FOP_DESCRIPTION]}
              label={t('tov_merchant_title')}
            >
              <TextArea />
            </FormItem>
            <StyledTitle>{t('fiscal_title')}</StyledTitle>
            <FormItem
              name={SettingsFormFields.FOP_CASH_REGISTER_ID}
              rules={rules[SettingsFormFields.FOP_CASH_REGISTER_ID]}
              label={t('fop_cash_register_id')}
            >
              <Input />
            </FormItem>
            <FormItem
              name={SettingsFormFields.FOP_CASHIER_NAME}
              rules={rules[SettingsFormFields.FOP_CASHIER_NAME]}
              label={t('fop_cashier_name')}
            >
              <Input />
            </FormItem>
            <FormItem
              name={SettingsFormFields.FOP_FISCAL_DEPARTMENT}
              rules={rules[SettingsFormFields.FOP_FISCAL_DEPARTMENT]}
              label={t('fop_fiscal_department')}
            >
              <Input />
            </FormItem>
            <FormItem
              name={SettingsFormFields.TOV_CASH_REGISTER_ID}
              rules={rules[SettingsFormFields.TOV_CASH_REGISTER_ID]}
              label={t('tov_cash_register_id')}
            >
              <Input />
            </FormItem>
            <FormItem
              name={SettingsFormFields.TOV_CASHIER_NAME}
              rules={rules[SettingsFormFields.TOV_CASHIER_NAME]}
              label={t('tov_cashier_name')}
            >
              <Input />
            </FormItem>
            <FormItem
              name={SettingsFormFields.TOV_FISCAL_DEPARTMENT}
              rules={rules[SettingsFormFields.TOV_FISCAL_DEPARTMENT]}
              label={t('tov_fiscal_department')}
            >
              <Input />
            </FormItem>
          </StyledBlock>
          <StyledBlock>
            <StyledLabel>{t('social_media')}</StyledLabel>
            <StyledBlock>
              {Object.values(SocialMedia).map((key) => (
                <FormItem name={key} label={t(`social_networks_${key}`, { ns: 'common' })} key={key} rules={rules[key]}>
                  <Input />
                </FormItem>
              ))}
            </StyledBlock>
          </StyledBlock>
        </StyledContainer>
      </Form>
    </StyledContentBlock>
  );
};

export default SettingsForm;
